/**
 * @element Text
 * @description Canonical typographic primitive for the whole dashboard.
 *
 * ONE type scale, expressed as named roles — so a "page headline" or a
 * "section sub-headline" looks identical on every screen instead of each
 * page hand-rolling `text-2xl font-semibold tracking-tight`. See the design
 * system doc (apps/documentation/docs/frontend/design-system.md → Typography)
 * for the role → usage mapping.
 *
 * Roles (canonical — prefer these going forward):
 *   display      hero / marketing H1 only          40–48px  semibold
 *   headline     page title (one per screen)        24–30px  semibold
 *   subheadline  section heading                    18–20px  semibold
 *   title        card / panel heading               16px     semibold
 *   eyebrow      micro label above a headline        11px     semibold caps
 *   lead         intro paragraph under a headline    16px     regular, relaxed
 *   paragraph    default body copy                   14px     regular
 *   small        caption / helper / meta             12px     regular
 *   error        inline validation message           14px     medium
 *   mono         code / ids / technical values       12px     mono
 *
 * Legacy aliases (kept so existing call-sites don't break — do NOT use in
 * new code): h1, h2, label, body.
 *
 * `as` picks the rendered element. Headlines SHOULD render as real heading
 * tags for accessibility; the default stays <span> for the inline roles.
 *
 * Usage:
 *   <Text as="h1" variant="headline">Billing</Text>
 *   <Text variant="lead">Manage plans, invoices and payment methods.</Text>
 *   <Text variant="paragraph">…</Text>
 */

import { mergeClasses } from '../../utils/classes.js';

// ── The scale. Neutral ink = var(--moksy-text) via the `neutral` token.
// ── The document scale ───────────────────────────────────────────────
//
// A REPORT is not a UI screen. Its body copy is set for continuous reading
// (15px / 28px line) where the app's `paragraph` role is a 14px UI size, so
// the report cannot simply borrow the UI roles without getting worse.
//
// It had no scale at all. One surface was rendering eleven sizes — 9, 10, 11,
// 12, 13, 14, 15, 16, 18, 24 and 30px — with equivalent content at different
// sizes in adjacent blocks: prose at 15px directly above a table at 14px, and
// three different "small" sizes (11, 12 and 14) doing the job of one caption.
//
// Six steps, exported as strings because the report renderers compose classes
// rather than nesting <Text>. Defined HERE so there is exactly one place a
// size is decided, and STYLES below reuses them rather than restating them.
// ⚠️ LEADING IS PART OF THE SCALE, NOT A LOOK.
//
// These classes are the web expression of packages/common/src/report/typescale.js
// and a test holds the two together — sizes AND line-heights. The line-height
// half was added after the browser showed body text set 15px/28px here and
// 11pt/1.6 in the PDF: the same paragraph, a third looser on screen. Nobody
// chose that; the size table agreed and nothing was checking the leading.
export const DOC_TYPE = {
  // 11px — the smallest thing a report is allowed to say. Stat labels,
  // provenance, "assumed" markers.
  eyebrow: 'text-[11px] leading-[15px] font-medium uppercase tracking-wide text-gray-400',
  // 13px — notes, secondary lines, anything subordinate to a body line.
  caption: 'text-[13px] leading-5 text-gray-500',
  // 15px — everything the reader actually reads. Prose, list items, key/value
  // lines, table cells. One size, so nothing shifts between blocks.
  body:    'text-[15px] leading-6 text-gray-700',
  // 15px, emphasised — a body line acting as a label (a timeline's phase, a
  // key in a key/value pair). Same SIZE as body on purpose: weight carries the
  // emphasis, so the line does not jump.
  bodyStrong: 'text-[15px] leading-6 font-medium text-neutral',
  // 15px, semibold, dark — the label ABOVE a block. Deliberately the same
  // size as the body it introduces: a label smaller than its own content
  // reads as a footnote, which is what a 14px label over 15px prose was
  // doing. Weight and ink carry the hierarchy instead of size.
  blockLabel: 'text-[15px] font-semibold leading-5 text-neutral',
  // A status pill. Joins the eyebrow step rather than adding a size of its own.
  badge:   'text-[11px] leading-[15px] font-medium',
  // The topic a run of rows share. Same size as the rows beneath it, set
  // apart by weight and by the rule above it — a heading that only gathers
  // four lines does not need to be bigger than them.
  topic:   'text-[15px] font-semibold leading-5 text-neutral',

  // ── figures, in three steps ──────────────────────────────────────
  //
  // A stat grid can only display what the generator puts in it, and what it
  // puts in it is not always a figure. A real report carried
  //   Timeframe / "10-14 weeks (MVP), iterated thereafter"
  // and set that 38-character sentence at 18px over an 11px label — the value
  // four times the visual weight of the thing naming it, which is what the
  // founder reported as "way bigger" and "not proportionate".
  //
  // One size cannot serve "£60k", "Payback within 12-18 months" and a
  // sentence. Three can, chosen by length — see figureRole in
  // packages/common/src/report/typescale.js, which is the single definition
  // these three classes are the web expression of.
  figureLg: 'text-[22px] font-semibold leading-tight text-neutral [font-variant-numeric:tabular-nums]',
  figureMd: 'text-[17px] font-semibold leading-snug text-neutral [font-variant-numeric:tabular-nums]',
  // A "figure" long enough to be prose IS prose, and is set as prose.
  figureSm: 'text-[15px] font-semibold leading-6 text-neutral',
  // The middle step under its old name, for the surfaces that ask for "a stat"
  // without a value to measure.
  stat:    'text-[17px] font-semibold leading-snug text-neutral',
  section: 'text-lg font-semibold leading-tight tracking-tight text-neutral',
  // A page-level standfirst under the title — body size, lighter ink.
  lead:    'text-[15px] leading-6 text-gray-600',
  // The document's own title.
  title:   'text-2xl font-semibold leading-tight tracking-tight text-neutral sm:text-3xl'
};

const STYLES = {
  // Canonical roles. Headline roles carry `font-primary` (Bebas Neue) — the
  // brand heading face; it upgrades from the system fallback once the .woff2
  // ships (see styles/index.css @font-face). `title` (card H3) deliberately
  // stays in the UI font: Bebas is condensed all-caps and hurts legibility at
  // a 16px card-label size.
  display:     'font-primary text-4xl md:text-5xl font-semibold tracking-tight text-neutral',
  headline:    'font-primary text-2xl md:text-3xl font-semibold tracking-tight text-neutral',
  subheadline: 'font-primary text-lg md:text-xl font-semibold tracking-tight text-neutral',
  title:       'text-base font-semibold text-neutral',
  eyebrow:     'block text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500',
  lead:        'text-base leading-relaxed text-gray-600',
  paragraph:   'text-sm leading-6 text-gray-600',
  small:       'block text-xs leading-5 text-gray-500',
  error:       'block text-sm font-medium leading-5 text-red-600',
  mono:        'block font-mono text-xs leading-5 text-gray-500',

  // Legacy aliases (unchanged visuals) — retained for back-compat only.
  h1:          'text-4xl font-semibold leading-tight tracking-tight text-neutral',
  h2:          'text-2xl font-semibold leading-tight tracking-tight text-neutral',
  label:       'block text-[11px] font-medium uppercase tracking-[0.18em] text-gray-500',
  body:        'text-sm leading-6 text-gray-600',
};

// ── Sensible default tag per role ────────────────────────────────────
//
// ⚠️ THIS TABLE IS WHY TEXT USED TO RUN TOGETHER.
//
// It used to list six roles. Every OTHER role — `body`, `small`, `h1`, `h2`,
// `error`, `mono` — fell through to the `'span'` default below and rendered
// `display:inline`. Stacked in a plain block container, as they are on almost
// every card in the app, two of them share a line:
//
//     <div>
//       <Text variant="body">Your tracks</Text>
//       <Text variant="body">Each track has its own ladder…</Text>
//     </div>
//
//   rendered → "Your tracksEach track has its own ladder…"
//
// That is a primitive-level defect, not a page-level one: it reached 723 call
// sites, and 136 of them had already paid the tax by hand-writing
// `className="block"` — the same fix, re-derived 136 times, and missed
// everywhere else. `h1`/`h2` were the worst of it: a 36px page title rendering
// as an inline <span>, so it was neither a block NOR a heading to a screen
// reader.
//
// Every role now names its tag. The rule is semantic, not cosmetic:
//   - headings render as real heading tags (a11y: they must be in the outline)
//   - prose roles render as <p>
//   - the label-ish roles stay <span> and carry `block` in their CLASS instead,
//     because a <span> is valid inside a <p>/<label>/<td> and a <p> is not —
//     these roles caption other content and must not risk invalid nesting.
const DEFAULT_TAG = {
  // Canonical roles.
  display: 'h1', headline: 'h1', subheadline: 'h2', title: 'h3',
  paragraph: 'p', lead: 'p',
  eyebrow: 'span', small: 'span', error: 'span', mono: 'span',
  // Legacy aliases. `h1`/`h2` are page and section titles: they get real
  // heading tags, which is what their type scale has always implied.
  h1: 'h1', h2: 'h2', label: 'span', body: 'p',
};

export const Text = ({ children, variant = 'paragraph', as, className = '', ...props }) => {
  const Tag = as || DEFAULT_TAG[variant] || 'span';
  const style = STYLES[variant] || STYLES.paragraph;
  // `${style} ${className}` does NOT make the call site win: two Tailwind
  // utilities in the same group have equal specificity, so the winner is
  // decided by STYLESHEET order, not by the class attribute. 293 call sites
  // here pass a className that re-declares a group its variant already sets —
  // the app footer ended up with `tracking-[0.18em]` and `tracking-tight` on
  // one span, computing to NEGATIVE tracking on 11px uppercase. mergeClasses
  // makes the rule the one everybody already assumes: the caller wins its own
  // group, the variant keeps the rest.
  return <Tag className={mergeClasses(style, className)} {...props}>{children}</Tag>;
};

export default Text;
